// 🔄 기기별 개별 처리 컴포넌트 - 관리자가 각 기기를 독립적으로 관리
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Paper, Box, Typography, Grid, Accordion, AccordionSummary, 
  AccordionDetails, TextField, FormControl, Select, MenuItem,
  Chip, IconButton, Divider, Alert, Tooltip, Button
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  DevicesOther as DeviceIcon,
  PriceCheck as PriceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { DeviceInfo } from '../../../types/application';
import { 
  validateDeviceStatusChange, 
  calculateDeviceStatusStats 
} from '../../../../../utils/applicationStatusCalculator';
import { getDeviceStatusColor } from '../../../../../constants/statusColors';


interface Props {
  devices: DeviceInfo[];
  onDeviceUpdate: (deviceId: string, updates: Partial<DeviceInfo>) => Promise<void>;
  onBatchDeviceUpdate?: (batchUpdates: Record<string, Partial<DeviceInfo>>) => Promise<void>;
  loading?: boolean;
  readonly?: boolean;
  onLocalChanges?: (hasChanges: boolean, pendingChanges: Record<string, Partial<DeviceInfo>>, saveAllChanges: () => Promise<void>) => void;
}

const DeviceProcessingList = ({ devices, onDeviceUpdate, onBatchDeviceUpdate, loading = false, readonly = false, onLocalChanges }: Props) => {
  const [expandedDevice, setExpandedDevice] = useState<string | false>(false);
  const [localLoading, setLocalLoading] = useState<Record<string, boolean>>({});
  
  // 🔧 로컬 변경사항 관리 (저장 버튼 눌러야 반영)
  const [localChanges, setLocalChanges] = useState<Record<string, Partial<DeviceInfo>>>({});
  
  // 🎯 비동기 상태 문제 해결을 위한 즉시 참조
  const localChangesRef = useRef<Record<string, Partial<DeviceInfo>>>({});
  
  // localChanges 상태와 ref 동기화
  useEffect(() => {
    localChangesRef.current = localChanges;
  }, [localChanges]);
  
  // 현재 기기 값 가져오기 (로컬 변경사항 우선 적용)
  const getCurrentDeviceValue = (deviceId: string, field: keyof DeviceInfo): any => {
    const localChange = localChanges[deviceId]?.[field];
    if (localChange !== undefined) return localChange;
    
    const device = devices.find(d => d.id === deviceId);
    return device?.[field];
  };
  
  // 모든 로컬 변경사항을 실제로 저장
  const saveAllChanges = useCallback(async () => {
    console.log(`💾 saveAllChanges 시작 - localChanges STATE:`, JSON.stringify(localChanges, null, 2));
    console.log(`💾 saveAllChanges 시작 - localChangesRef CURRENT:`, JSON.stringify(localChangesRef.current, null, 2));
    
    // 🎯 ref를 사용해서 최신 상태 참조
    const changesToSave = { ...localChangesRef.current };
    console.log(`💾 changesToSave 복사 완료 (ref 사용):`, JSON.stringify(changesToSave, null, 2));
    
    if (Object.keys(changesToSave).length === 0) {
      console.log(`📭 저장할 변경사항 없음`);
      return;
    }
    
    try {
      setLocalLoading(prev => ({ ...prev, _saving: true }));
      
      // 🎯 강제 배치 업데이트로 상태 충돌 완전 방지
      console.log(`💾 총 ${Object.keys(changesToSave).length}개 기기를 배치로 업데이트 시작`);
      console.log(`💾 배치 업데이트 데이터:`, JSON.stringify(changesToSave, null, 2));
      
      // 🚀 배치 함수 전달 문제 우회 - 직접 배치 처리
      if (typeof onBatchDeviceUpdate === 'function') {
        console.log(`🚀 정상 배치 업데이트 함수 사용`);
        await onBatchDeviceUpdate(changesToSave);
        console.log(`✅ 정상 배치 업데이트 완료`);
      } else {
        console.log(`🔄 직접 배치 처리 - 상태 충돌 방지`);
        
        // 🎯 직접 배치 업데이트 처리 (Firestore 한 번만 업데이트)
        if (!devices || devices.length === 0) {
          console.error(`❌ 기기 배열이 없습니다`);
          return;
        }
        
        // 1. 현재 devices 배열 복사
        let updatedDevices = [...devices];
        console.log(`📊 원본 기기 배열:`, updatedDevices.map(d => ({ id: d.id, model: d.deviceModel, price_offer: d.price_offer })));
        
        // 2. 모든 변경사항 한 번에 적용
        for (const [deviceId, updates] of Object.entries(changesToSave)) {
          const deviceIndex = updatedDevices.findIndex(d => d.id === deviceId);
          if (deviceIndex !== -1) {
            updatedDevices[deviceIndex] = {
              ...updatedDevices[deviceIndex],
              ...updates,
              updated_at: new Date()
            };
            console.log(`📝 기기 ${deviceIndex + 1} 업데이트: ${JSON.stringify(updates)}`);
          }
        }
        
        console.log(`📋 최종 기기 배열:`, updatedDevices.map(d => ({ id: d.id, model: d.deviceModel, price_offer: d.price_offer })));
        
        // 3. 첫 번째 기기만 실제 업데이트 (전체 배열 포함)
        const firstDeviceId = Object.keys(changesToSave)[0];
        const combinedUpdates = {
          ...changesToSave[firstDeviceId],
          _batchDevices: updatedDevices, // 🎯 전체 기기 배열 전달
          updated_at: new Date()
        };
        
        console.log(`🎯 배치 업데이트 실행 - 첫 번째 기기로 전체 처리`);
        await onDeviceUpdate(firstDeviceId, combinedUpdates);
        console.log(`✅ 직접 배치 처리 완료`);
      }
      
      // 저장 성공 시 로컬 변경사항 초기화
      setLocalChanges({});
      onLocalChanges?.(false, {}, saveAllChanges);
      
    } catch (error) {
      console.error('Failed to save device changes:', error);
      throw error;
    } finally {
      setLocalLoading(prev => {
        const newLoading = { ...prev };
        delete newLoading._saving;
        return newLoading;
      });
    }
  }, [localChanges, onDeviceUpdate, onLocalChanges]);
  
  // 로컬 변경사항 업데이트 (Race Condition 방지)
  const updateLocalChange = (deviceId: string, field: keyof DeviceInfo, value: any) => {
    console.log(`📝 updateLocalChange BEFORE: deviceId=${deviceId}, field=${field}, value=${value}, type=${typeof value}`);
    const device = devices.find(d => d.id === deviceId);
    if (!device) return;
    
    // 함수형 업데이트로 Race Condition 방지
    setLocalChanges(prev => {
      const currentDeviceChanges = prev[deviceId] || {};
      
      // 원본 값과 같으면 해당 필드 제거
      if (device[field] === value) {
        const newDeviceChanges = { ...currentDeviceChanges };
        delete newDeviceChanges[field];
        
        let newChanges;
        if (Object.keys(newDeviceChanges).length === 0) {
          // 기기의 모든 변경사항이 없으면 기기 자체 제거
          newChanges = { ...prev };
          delete newChanges[deviceId];
        } else {
          newChanges = {
            ...prev,
            [deviceId]: newDeviceChanges
          };
        }
        
        // 🎯 ref도 즉시 업데이트
        localChangesRef.current = newChanges;
        
        console.log(`📝 updateLocalChange 필드 제거:`, newChanges);
        console.log(`📝 ref 필드 제거 업데이트:`, localChangesRef.current);
        
        const hasChanges = Object.keys(newChanges).length > 0;
        onLocalChanges?.(hasChanges, newChanges, saveAllChanges);
        
        return newChanges;
      } else {
        // 변경사항 추가 (기존 값 보존)
        const newChanges = {
          ...prev,
          [deviceId]: {
            ...currentDeviceChanges,
            [field]: value
          }
        };
        
        // 🎯 ref도 즉시 업데이트
        localChangesRef.current = newChanges;
        
        console.log(`📝 updateLocalChange 안전한 추가 - ${field}: ${value}`, newChanges);
        console.log(`📝 해당 기기 최종 상태:`, newChanges[deviceId]);
        console.log(`📝 ref 즉시 업데이트:`, localChangesRef.current[deviceId]);
        
        onLocalChanges?.(true, newChanges, saveAllChanges);
        
        return newChanges;
      }
    });
  };
  
  const stats = calculateDeviceStatusStats(devices);

  // 🔄 새로운 상태별 색상 매핑
  const getStatusColor = (status: DeviceInfo['status']) => {
    return getDeviceStatusColor(status);
  };
  
  // 🔄 새로운 상태별 한글 라벨
  const getStatusLabel = (status: DeviceInfo['status']) => {
    return status; // 이미 한글로 정의되어 있음
  };

  // 🔄 새로운 상태별 아이콘
  const getStatusIcon = (status: DeviceInfo['status']): React.ReactElement | undefined => {
    switch (status) {
      case '완료':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case '반송':
        return <CancelIcon sx={{ fontSize: 16 }} />;
      case '가격안내':
        return <PriceIcon sx={{ fontSize: 16 }} />;
      case '검수중':
        return <SyncIcon sx={{ fontSize: 16 }} />;
      case '입금대기':
        return <CheckCircleIcon sx={{ fontSize: 16, color: '#26a69a' }} />;
      default:
        return undefined;
    }
  };

  // 🔧 개별 기기 업데이트 핸들러 (간소화)
  const handleDeviceChange = async (deviceId: string, field: keyof DeviceInfo, value: any) => {
    if (readonly || loading || localLoading[deviceId]) return;
    
    setLocalLoading(prev => ({ ...prev, [deviceId]: true }));
    
    try {
      const updates: Partial<DeviceInfo> = { [field]: value, updated_at: new Date() };
      
      // 상태 변경 시 유효성 검증
      if (field === 'status') {
        const device = devices.find(d => d.id === deviceId);
        if (device) {
          const validation = validateDeviceStatusChange(device, value);
          if (!validation.isValid) {
            alert(validation.error);
            return;
          }
        }
      }
      
      console.log(`🔧 Updating device ${deviceId}, field: ${String(field)}, value:`, value);
      await onDeviceUpdate(deviceId, updates);
      console.log(`✅ Device ${deviceId} updated successfully`);
      
    } catch (error) {
      console.error(`❌ Device update failed for ${deviceId}:`, error);
      alert(`기기 정보 업데이트에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    } finally {
      setLocalLoading(prev => ({ ...prev, [deviceId]: false }));
    }
  };

  // 아코디언 토글 핸들러
  const handleAccordionChange = (deviceId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedDevice(isExpanded ? deviceId : false);
  };

  if (!devices || devices.length === 0) {
    return (
      <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #e0e7ef', p: 3 }}>
        <Alert severity="info" sx={{ bgcolor: '#f0f9ff' }}>
          등록된 기기가 없습니다.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #e0e7ef' }}>
      {/* 헤더 및 통계 */}
      <Box sx={{ p: 2.5, borderBottom: '1px solid #e0e7ef' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <DeviceIcon sx={{ mr: 1, fontSize: 20 }} />
            기기별 처리 현황 ({devices.length}개)
          </Typography>
          
          {!readonly && (
            <Tooltip title="모든 입금대기 기기를 완료로 일괄 변경">
              <Button
                size="small"
                variant="outlined"
                startIcon={<CheckCircleIcon />}
                onClick={() => {
                  devices.forEach(device => {
                    if (device.status === '입금대기') {
                      handleDeviceChange(device.id, 'status', '완료');
                    }
                  });
                }}
                disabled={loading || stats.입금대기 === 0}
                sx={{ fontSize: '0.75rem' }}
              >
                일괄 완료 처리
              </Button>
            </Tooltip>
          )}
        </Box>
        
        {/* 🔄 새로운 상태별 통계 */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {stats.검수중 > 0 && (
            <Chip 
              label={`검수중 ${stats.검수중}`} 
              size="small" 
              sx={{ bgcolor: getStatusColor('검수중'), color: 'white', fontSize: '0.7rem' }} 
            />
          )}
          {stats.가격안내 > 0 && (
            <Chip 
              label={`가격안내 ${stats.가격안내}`} 
              size="small" 
              sx={{ bgcolor: getStatusColor('가격안내'), color: 'white', fontSize: '0.7rem' }} 
            />
          )}
          {stats.입금대기 > 0 && (
            <Chip 
              label={`입금대기 ${stats.입금대기}`} 
              size="small" 
              sx={{ bgcolor: getStatusColor('입금대기'), color: 'white', fontSize: '0.7rem' }} 
            />
          )}
          {stats.완료 > 0 && (
            <Chip 
              label={`완료 ${stats.완료}`} 
              size="small" 
              sx={{ bgcolor: getStatusColor('완료'), color: 'white', fontSize: '0.7rem' }} 
            />
          )}
          {stats.반송 > 0 && (
            <Chip 
              label={`반송 ${stats.반송}`} 
              size="small" 
              sx={{ bgcolor: getStatusColor('반송'), color: 'white', fontSize: '0.7rem' }} 
            />
          )}
        </Box>
      </Box>
      
      {/* 기기 목록 */}
      <Box sx={{ p: 0.5 }}>
        {devices.map((device, index) => {
          const isDeviceLoading = localLoading[device.id] || loading;
          
          return (
            <Accordion 
              key={device.id || index}
              expanded={expandedDevice === device.id}
              onChange={handleAccordionChange(device.id)}
              sx={{ 
                boxShadow: 'none',
                border: 'none',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { 
                  backgroundColor: '#f8fafc',
                  borderRadius: 2,
                  margin: '4px 8px'
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  minHeight: 60,
                  '&.Mui-expanded': { minHeight: 60 },
                  opacity: isDeviceLoading ? 0.6 : 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                      📱 {device.deviceType} - {device.deviceModel}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={getStatusIcon(device.status)}
                        label={getStatusLabel(device.status)}
                        size="small"
                        sx={{ 
                          backgroundColor: getStatusColor(device.status),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                      {device.price_offer && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                          💰 검수: <strong>₩{device.price_offer.toLocaleString()}</strong>
                        </Typography>
                      )}
                      {device.final_price && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                          ✅ 최종: <strong>₩{device.final_price.toLocaleString()}</strong>
                        </Typography>
                      )}
                      {device.grade && (
                        <Chip 
                          label={device.grade} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ pt: 0 }}>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {/* 상태 변경 */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                        처리 상태
                      </Typography>
                                                    <Select
                    value={getCurrentDeviceValue(device.id, 'status')}
                    onChange={(e) => updateLocalChange(device.id, 'status', e.target.value)}
                    disabled={readonly || isDeviceLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: localChanges[device.id]?.status !== undefined ? '#fff3cd' : 'transparent'
                      }
                    }}
                  >
              <MenuItem value="접수완료">📋 접수완료</MenuItem>
              <MenuItem value="택배접수">📦 택배접수</MenuItem>
              <MenuItem value="입고완료">📥 입고완료</MenuItem>
              <MenuItem value="검수중">🔍 검수중</MenuItem>
              <MenuItem value="가격안내">💰 가격안내</MenuItem>
              <MenuItem value="입금대기">✅ 입금대기</MenuItem>
              <MenuItem value="반송">❌ 반송</MenuItem>
              <MenuItem value="완료">💸 완료</MenuItem>
            </Select>
                    </FormControl>
                  </Grid>

                  {/* 등급 */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                        등급
                      </Typography>
                      <Select
                        value={getCurrentDeviceValue(device.id, 'grade') || ''}
                        onChange={(e) => updateLocalChange(device.id, 'grade', e.target.value || null)}
                        disabled={readonly || isDeviceLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: localChanges[device.id]?.grade !== undefined ? '#fff3cd' : 'transparent'
                          }
                        }}
                      >
                        <MenuItem value="">등급 선택</MenuItem>
                        <MenuItem value="A">🟢 A등급</MenuItem>
                        <MenuItem value="B">🔵 B등급</MenuItem>
                        <MenuItem value="C">🟡 C등급</MenuItem>
                        <MenuItem value="D">🔴 D등급</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* 검수 가격 */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
                      검수 가격
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      type="text"
                      inputProps={{ 
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                      value={getCurrentDeviceValue(device.id, 'price_offer') || ''}
                      onChange={(e) => {
                        // 숫자만 허용
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        const numericValue = value === '' ? null : parseInt(value, 10);
                        console.log(`🔧 price_offer 입력: "${e.target.value}" → 필터링: "${value}" → 숫자: ${numericValue}`);
                        updateLocalChange(device.id, 'price_offer', numericValue);
                      }}
                      onWheel={(e) => (e.target as HTMLElement).blur()} // 마우스 휠로 값 변경 방지
                      disabled={readonly || localLoading[device.id]}
                      InputProps={{
                        startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>₩</Typography>,
                      }}
                      placeholder="검수 가격 입력"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: localChanges[device.id]?.price_offer !== undefined ? '#fff3cd' : 'transparent'
                        }
                      }}
                    />
                  </Grid>

                  {/* 최종 가격 */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
                      최종 가격
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      type="text"
                      inputProps={{ 
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                      value={getCurrentDeviceValue(device.id, 'final_price') || ''}
                      onChange={(e) => {
                        // 숫자만 허용
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        const numericValue = value === '' ? null : parseInt(value, 10);
                        console.log(`🔧 final_price 입력: "${e.target.value}" → 필터링: "${value}" → 숫자: ${numericValue}`);
                        updateLocalChange(device.id, 'final_price', numericValue);
                      }}
                      onWheel={(e) => (e.target as HTMLElement).blur()} // 마우스 휠로 값 변경 방지
                      disabled={readonly || localLoading[device.id]}
                      InputProps={{
                        startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>₩</Typography>,
                      }}
                      placeholder="최종 가격 입력"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: localChanges[device.id]?.final_price !== undefined ? '#fff3cd' : 'transparent'
                        }
                      }}
                    />
                  </Grid>

                  {/* 차감 사유 (모든 등급에서 선택적 입력 가능) */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
                      차감 사유 (선택)
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={getCurrentDeviceValue(device.id, 'deduction_reason') || ''}
                      onChange={(e) => updateLocalChange(device.id, 'deduction_reason', e.target.value || null)}
                      disabled={readonly || isDeviceLoading}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: 2, 
                          bgcolor: localChanges[device.id]?.deduction_reason !== undefined ? '#fff3cd' : 'transparent'
                        } 
                      }}
                      placeholder="차감 사유가 있으면 입력하세요"
                    />
                  </Grid>

                  {/* 반송일 때만 반송 사유 표시 */}
                  {getCurrentDeviceValue(device.id, 'status') === '반송' && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="error" sx={{ mb: 0.5, fontWeight: 500, display: 'block' }}>
                        반송 사유 <WarningIcon sx={{ fontSize: 14, ml: 0.5 }} />
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={getCurrentDeviceValue(device.id, 'reject_reason') || ''}
                        onChange={(e) => updateLocalChange(device.id, 'reject_reason', e.target.value || null)}
                        disabled={readonly || isDeviceLoading}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            bgcolor: localChanges[device.id]?.reject_reason !== undefined ? '#fff3cd' : '#fef2f2'
                          } 
                        }}
                        placeholder="반송 사유를 입력하세요"
                      />
                    </Grid>
                  )}

                  {/* 기기 업데이트 시간 표시 */}
                  {device.updated_at && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        마지막 수정: {new Date(device.updated_at.seconds ? device.updated_at.seconds * 1000 : device.updated_at).toLocaleString('ko-KR')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Paper>
  );
};

export default DeviceProcessingList;